import { useContext, useEffect, useRef, useState } from "react";

import config from "@/config";
import ProfileDataContext from "@/context/ProfileDataContext";
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from "@/services/fetchWrapper.service"; // ← make sure this is imported
import ContinueButton from '@/shared-components/buttons/components/ContinueButton';
import XIcon from "@/shared-components/icons/components/public/XIcon";
import Loader from "@/shared-components/loader/Loader";
import PopupToBottomSheet from "@/shared-components/popups/components/PopupToBottomSheet";
import Text from "@/shared-components/text/components/Text";

import ArticleRow from "./ArticleRow";

import "./ArticleRecommendations.css";

export default function ArticleRecommendations({
  open,
  close,
  selected,
  onChange,
  maxItems = 3,
  galleryId,
  currentArticleId,
}) {
  const t = useTranslations('common.forms.board_editor');
  // (We're not actually using profileData in this component right now,
  // but we keep the context import in case you need it later.)

  const { profileData } = useContext(ProfileDataContext);
  const userType = profileData?.user_type;   // pull gallery vs. artist from context

  const [myArticles, setMyArticles] = useState([]);     // all articles we've loaded so far
  const [offset, setOffset] = useState(0);              // the next offset to fetch
  const offsetRef = useRef(0);                          // mirror of `offset` for scroll‐handler
  const [isFetching, setIsFetching] = useState(false);  // are we mid‐fetch?
  const [hasMore, setHasMore] = useState(true);         // do we think more pages exist?
  const listContainerRef = useRef(null);

  // Keep track of the *previous* `open` prop so we only run page‐1 once when open flips true
  const prevOpen = useRef(false);

  // ── EFFECT: when open goes from false → true, reset state + fetch page 1 ──
  useEffect(() => {
    // If we just opened (false→true), reset everything & load offset=0.
    if (open && !prevOpen.current) {
      setMyArticles([]);
      setOffset(0);
      offsetRef.current = 0;
      setHasMore(true);

      fetchNextPage(0);
    }
    prevOpen.current = open;
  }, [open]);

  // ── fetchNextPage: load one "page" of 10 articles starting at currentOffset ──
  async function fetchNextPage(currentOffset) {
    // Don't do another fetch if we're already fetching or we know there are no more pages
    if (isFetching || !hasMore) return;
    setIsFetching(true);

    try {
      const pageSize = 10;
      // Manually build the URL using the correct userType (gallery|artist)
      const url = [
        `${config.apis.api.url}/posts/${userType}/articles/${galleryId}`,
        `offset=${currentOffset}`,
        `limit=${pageSize}`,
      ].join("?");

      // Use fetchWrapper.get(url, { injectToken: true })
      const resp = await fetchWrapper.get(url, { injectToken: true });

      // ─── NEW: tolerate either `resp.data.data` or `resp.data.docs` depending on your actual payload
      let chunk = [];
      if (Array.isArray(resp.data?.data)) {
        chunk = resp.data.data;
      } else if (Array.isArray(resp.data?.docs)) {
        chunk = resp.data.docs;
      } else {
        chunk = [];
      }

      // If empty → no more pages
      if (chunk.length === 0) {
        setHasMore(false);
        setIsFetching(false);
        return;
      }

      // Filter out the "currentArticleId" if we're editing an existing article
      const filteredChunk = chunk.filter((a) => a._id !== currentArticleId);

      // Append to our array
      setMyArticles((prev) => {
        return [...prev, ...filteredChunk];
      });

      // If we got fewer than 10, we're at the last page; otherwise bump offset
      if (chunk.length < pageSize) {
        setHasMore(false);
      } else {
        setOffset((prev) => {
          const newOffset = prev + pageSize;
          offsetRef.current = newOffset;
          return newOffset;
        });
      }

      setIsFetching(false);
    } catch (err) {
      console.error("❌ [AR] fetch error:", err);
      setIsFetching(false);
    }
  }

  // ── Scroll handler: fire when the user scrolls near the bottom ──
  function handleScroll() {
    if (!listContainerRef.current || isFetching || !hasMore) return;

    const container = listContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Don't load "page 2" until the user scrolls down at least a bit
    if (scrollTop === 0) return;
    // Must overflow to even consider loading more
    if (scrollHeight <= clientHeight) return;

    // If within 50px of the bottom, load the next page
    if (scrollTop + clientHeight + 50 >= scrollHeight) {
      fetchNextPage(offsetRef.current);
    }
  }

  // If the sheet is closed, render nothing
  if (!open) return null;

  const isOpening = !prevOpen.current;
  const isInitialLoading = isOpening || !galleryId || (offset === 0 && isFetching);

  return (
    <PopupToBottomSheet
      open={open}
      close={close}
      popupOptions={{ opacity: true }}
      className="article-recs-popup-bottom-sheet__container"
    >
      <div className={`article-recs__content-container${isInitialLoading ? ' article-recs__content-container--loading' : ''}`}>
        {isInitialLoading ? (
          <div aria-label={t('loading_articles')} aria-live="polite" role="status">
            <Loader inline />
          </div>
        ) : (
          <>
            <div className="article-recs__header">
              <Text className="article-recs__header-text">
                {t('recommendations_title')}
              </Text>
              <XIcon
                className="article-recs__close-button"
                onClick={close}
              />
            </div>

            <div
              className="article-recs__vertical-list"
              ref={listContainerRef}
              onScroll={handleScroll}
            >
              {myArticles.map((article) => (
                <ArticleRow
                  key={article._id}
                  article={article}
                  isSelected={selected.includes(article._id)}
                  onToggle={(id) => {
                    let updated;
                    if (selected.includes(id)) {
                      updated = selected.filter((x) => x !== id);
                    } else {
                      if (selected.length >= maxItems) return;
                      updated = [...selected, id];
                    }
                    onChange(updated);
                  }}
                  closePopup={close}
                />
              ))}

              {myArticles.length === 0 && !isFetching && !hasMore && (
                <Text className="article-recs__empty font-size-medium" paragraph>
                  {t('no_articles')}
                </Text>
              )}

              {isFetching && offset > 0 && (
                <div style={{ padding: "1rem", textAlign: "center" }}>
                  <Loader />
                </div>
              )}

              {!hasMore && myArticles.length > 0 && (
                <div style={{ padding: "0.5rem 1rem", textAlign: "center" }}>
                  <Text className="article-recs__end-message" paragraph>
                    {t('end_of_articles')}
                  </Text>
                </div>
              )}
            </div>

            <div className="article-recs__footer">
              <ContinueButton
                onClick={close}
                turnOff={selected.length === 0}
                type="button"
              >
                {t('done', { selected: selected.length, max: maxItems })}
              </ContinueButton>
            </div>
          </>
        )}
      </div>
    </PopupToBottomSheet>
  );
}
